import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { DateTime } from 'luxon' 
import { formatPrice } from '@/utils/formatters'
import { Input, Select, Toggle } from '@/components/ui'
import { SingleLineAddress } from '@/components/Address'
import { formatBienLabel } from '../biens/biensSlice'
import { calcMontantTotal, selectQuittanceById, setRentMontants, updateRent, MODES_REGLEMENT } from './quittancesSlice'
import QuittanceAdditionalLines from './QuittanceAdditionalLines'

export default ({id, onClose = () => {}, onSuccess = () => {}}) => {
  const dispatch = useDispatch()
  const quittance = useSelector(state => selectQuittanceById(state, id))
  const errors = useSelector((state) => state.quittances.requestStatus.errors)
  const isUpdating = useSelector((state) => state.quittances.loadingStatus.updateRent)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const warningFieldNames = useRef(new Set())
  
  // Obtenir le dernier jour du mois courant pour la date de délivrance par défaut
  const lastDayOfMonth = DateTime.now().endOf('month') 
  
  const { handleSubmit, reset, control, setValue, watch, formState: { errors: formErrors } } = useForm({
    defaultValues: {},
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const montant_loyer = watch("montant_loyer")
  const montant_charges = watch("montant_charges")

  // Synchroniser le formulaire lorsque visite change 
  useEffect(() => {
    if (!quittance) return;
    if(quittance.month !== DateTime.fromISO(lastDayOfMonth).month) {
      setValue("date_delivrance", DateTime.now().toISODate())
      warningFieldNames.current = new Set([
        ...warningFieldNames.current,
        "date_delivrance"
      ])

    } else {
      setValue("date_delivrance", lastDayOfMonth)
    }

    setValue("montant_loyer", quittance.montant_loyer)
    setValue("montant_charges", quittance.montant_charges) 
    setValue("date_paiement", quittance.date_paiement)
    setValue("mode_reglement", quittance.mode_reglement || "virement")
  }, [quittance]) 
  
  useEffect(() => {
    if(!quittance) return;
    if(montant_loyer !== quittance.montant_loyer) {
      dispatch(setRentMontants({id: quittance.id, montant_loyer }))
    }
    if(montant_charges !== quittance.montant_charges) {
      dispatch(setRentMontants({id: quittance.id, montant_charges}))
    }
  }, [montant_loyer, montant_charges])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        reset()
        onSuccess()
      }
    }
  }, [isUpdating, errors, onSuccess, reset])
  
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)
      
      formData.additional_lines = quittance.additional_lines

      await dispatch(updateRent({
        id: quittance.id,
        data: formData
      }))
    } catch (error) {
      setIsSubmitting(false)
    }
  }
  
  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if(Array.isArray(errorsMsg)) {
      return errorsMsg.join(", ")
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  // Formater la date (mois et année)
  const formatPeriod = (year, month) => {
    const date = new Date(year, month - 1)
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Appel à quittance</h2>
        </div>
        
        { !quittance ? (
          <p className="text-gray-500 mb-4">Aucune quittance sélectionnée</p>
        ) : (
          <>
            <div className="card bg-base-100 p-4 mb-4 border border-primary-200 rounded-none min-h-[450px] overflow-y-auto">
              <div className="space-y-6">
                <div className="p-2">

                  <div className="flex items-center mb-4">
                    <span className="font-medium"> 
                      {quittance.locataires && quittance.locataires.length > 0
                        ? quittance.locataires.map(l => l.nom_complet).join(', ')
                        : 'Locataire inconnu'} -
                      <span className="ml-2">{formatPeriod(quittance.year, quittance.month) || 'Non spécifiée'}</span>
                    </span>
                    <div className="text-sm font-bold ml-4">
                      <SingleLineAddress data={quittance.bien} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Input.Currency 
                        name={`montant_loyer`}
                        label="Montant du loyer"
                        control={control}
                        rules={{
                          required: "Le montant du loyer est requis."
                        }}
                        validator={{hasError, getErrorMessage}}
                      />
                      <Input.Currency 
                        name={`montant_charges`}
                        label="Montant des charges"
                        control={control}
                        rules={{
                          required: "Le montant des charges est requis."
                        }}
                        validator={{hasError, getErrorMessage}}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Input.Date
                        name={`date_paiement`}
                        label="Date de paiement"
                        control={control}
                        rules={{
                          required: "La date de paiement est requise"
                        }}
                        validator={{hasError, getErrorMessage}}
                      />
                      <div>
                        <Input.Date
                          name={`date_delivrance`}
                          label="Date de délivrance"
                          control={control}
                          rules={{
                            required: "La date de délivrance est requise"
                          }}
                          validator={{hasError, getErrorMessage}}
                        />
                        { warningFieldNames.current.has(`date_delivrance`) && 
                          <div className="text-warning text-sm">Penser à vérifier la date de délivrance !</div>
                        }
                      </div>
                    </div>

                    <div className="">
                      <Select.Form
                        name={`mode_reglement`}
                        control={control}
                        label="Mode de règlement"
                        options={MODES_REGLEMENT}
                        validator={{hasError, getErrorMessage}}
                      />
                      <Toggle.Form 
                        name={`force_notification_to_locataires`}
                        control={control}
                        label="Envoyé aussitôt au(x) locataire(s)"
                      />
                    </div>

                  </div>
                </div>
              </div>

              <QuittanceAdditionalLines quittance={quittance} />

              <div>
                <div className="divider"></div>
                <label className="input border-none currency-input flex justify-between">
                  <span className="label">Montant total</span>
                  <span className="replace-input-end-label">{formatPrice(calcMontantTotal(quittance))}</span>
                </label>
              </div>
            </div> 

            <div className="flex justify-between">
              <button
                type="button"
                className="btn btn-outline"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={`btn ${isUpdating ? 'loading' : ''} btn-primary text-white p-2`}
                disabled={isUpdating}
              >
                {isUpdating ? 'Génération...' : 'Générer les quittances'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
